import React, { useMemo } from 'react'
import { Box, Typography, LinearProgress, alpha } from '@mui/material'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'
import TabletIcon from '@mui/icons-material/Tablet'
import ComputerIcon from '@mui/icons-material/Computer'
import { AggregatedMetrics } from '../../hooks/useAnalytics'

interface DeviceBreakdownProps {
  metrics: AggregatedMetrics
  title?: string
}

export default function DeviceBreakdown({
  metrics,
  title = 'Enheder',
}: DeviceBreakdownProps) {
  const data = useMemo(() => {
    const total = metrics.views_mobile + metrics.views_desktop + metrics.views_tablet
    if (total === 0) return []
    
    return [
      {
        name: 'Desktop',
        value: metrics.views_desktop,
        percentage: (metrics.views_desktop / total) * 100,
        icon: <ComputerIcon />,
        color: '#5c4b99',
      },
      {
        name: 'Mobil',
        value: metrics.views_mobile,
        percentage: (metrics.views_mobile / total) * 100,
        icon: <PhoneIphoneIcon />,
        color: '#16a34a',
      },
      {
        name: 'Tablet',
        value: metrics.views_tablet,
        percentage: (metrics.views_tablet / total) * 100,
        icon: <TabletIcon />,
        color: '#0284c7',
      },
    ].filter((d) => d.value > 0)
  }, [metrics])

  if (data.length === 0) {
    return (
      <Box
        sx={{
          p: 3,
          textAlign: 'center',
          backgroundColor: 'background.paper',
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'divider',
        }}
      >
        <Typography color="text.secondary">
          Ingen enhedsdata tilgængelig
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      {title && (
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {title}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {data.map((device) => (
          <Box key={device.name}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ color: device.color, display: 'flex' }}>
                  {device.icon}
                </Box>
                <Typography variant="body2" fontWeight={500}>
                  {device.name}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  {device.value.toLocaleString('da-DK')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ({device.percentage.toFixed(1)}%)
                </Typography>
              </Box>
            </Box>
            <LinearProgress
              variant="determinate"
              value={device.percentage}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: alpha(device.color, 0.15),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  backgroundColor: device.color,
                },
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  )
}
